'use client'

import {useEffect, useMemo, useRef, useState} from 'react'
import type {Contact, SettingsState} from '@/types'
import {avatarDisplaySrc, displayName, filterContacts} from '@/lib/utils'
import {PageHeader} from '@/components/PageHeader'
import {Switch} from '@/components/Switch'

export function GroupWelcomeView({
    groups,
    settings,
    saveSettings,
}: {
    groups: Contact[]
    settings: SettingsState
    saveSettings: (patch: Partial<SettingsState>) => void
}) {
    const [selectedWxid, setSelectedWxid] = useState('')
    const [query, setQuery] = useState('')
    const selected = groups.find((group) => group.wxid === selectedWxid) || groups[0] || null
    const savedConfig = selected ? settings.groupWelcome[selected.wxid] : null
    const [draft, setDraft] = useState<{enabled: boolean; content: string}>({enabled: false, content: ''})
    const textareaRef = useRef<HTMLTextAreaElement | null>(null)
    const filteredGroups = useMemo(() => filterContacts(groups, query), [groups, query])

    useEffect(() => {
        if (!selected) {
            setDraft({enabled: false, content: ''})
            return
        }
        setDraft(
            savedConfig || {
                enabled: false,
                content: '欢迎 @新人 加入群聊！',
            },
        )
    }, [savedConfig, selected])

    const handleSave = () => {
        if (!selected) return
        saveSettings({groupWelcome: {...settings.groupWelcome, [selected.wxid]: draft}})
    }

    const insertPlaceholder = (placeholder: string) => {
        const textarea = textareaRef.current
        const current = draft.content
        const start = textarea?.selectionStart ?? current.length
        const end = textarea?.selectionEnd ?? start
        const insertion = `${placeholder} `
        const nextContent = `${current.slice(0, start)}${insertion}${current.slice(end)}`
        const nextCursor = start + insertion.length

        setDraft((prev) => ({...prev, content: nextContent}))
        requestAnimationFrame(() => {
            textareaRef.current?.focus()
            textareaRef.current?.setSelectionRange(nextCursor, nextCursor)
        })
    }

    return (
        <section className="flex flex-col h-full bg-page">
            <PageHeader title="入群欢迎语"/>
            <div className="flex-1 flex flex-col md:flex-row overflow-y-auto md:overflow-hidden p-3 md:p-6 gap-3 md:gap-6">
                <div className="w-full md:w-[298px] shrink-0 bg-white rounded-card shadow-card border border-hair flex flex-col overflow-hidden h-auto md:h-full">
                    <div className="p-4 border-b border-hair bg-gray-50 font-medium text-ink shrink-0">选择群聊</div>
                    <div className="p-3 border-b border-hair bg-page shrink-0">
                        <input
                            type="text"
                            value={query}
                            onChange={(event) => setQuery(event.target.value)}
                            placeholder="搜索群聊"
                            className="w-full h-8 bg-white border border-hair rounded px-3 text-sm text-ink placeholder:text-muted focus:outline-none focus:border-accent focus:ring-1 focus:ring-accent/30"
                        />
                    </div>
                    <div className="flex-1 overflow-y-auto">
                        {filteredGroups.length ? (
                            filteredGroups.map((group) => {
                                const isActive = selected?.wxid === group.wxid
                                return (
                                    <button
                                        type="button"
                                        key={group.wxid}
                                        onClick={() => setSelectedWxid(group.wxid)}
                                        className={`flex items-center gap-3 p-3 w-full text-left border-b border-hair/40 transition-colors ${
                                            isActive ? 'bg-subActive text-ink' : 'text-soft hover:bg-subHover'
                                        }`}
                                    >
                                        <img
                                            src={avatarDisplaySrc(group.avatar, group.wxid)}
                                            alt={displayName(group)}
                                            className="w-10 h-10 rounded bg-gray-100 shrink-0"
                                        />
                                        <span className="text-sm truncate">{displayName(group)}</span>
                                    </button>
                                )
                            })
                        ) : (
                            <div className="text-center text-sm text-muted py-10">{query ? '没有匹配的群聊' : '暂无群聊'}</div>
                        )}
                    </div>
                </div>
                <div className="flex-1 min-h-[320px] md:min-h-0 bg-white rounded-card shadow-card border border-hair p-4 md:p-5 h-auto md:h-full overflow-y-auto flex flex-col">
                    {selected ? (
                        <>
                            <div className="border-b border-hair pb-4 mb-4 flex justify-between items-center">
                                <h2 className="text-base font-medium text-ink truncate">
                                    {displayName(selected)} - 欢迎语设置
                                </h2>
                                <div className="flex items-center gap-2 shrink-0">
                                    <span className="text-sm text-muted">启用欢迎语</span>
                                    <Switch
                                        checked={draft.enabled}
                                        onChange={() => setDraft({...draft, enabled: !draft.enabled})}
                                    />
                                </div>
                            </div>
                            <label className="block text-sm text-soft mb-2">当有新成员加入时，自动发送以下消息：</label>
                            <div className="mb-2 flex flex-wrap gap-2">
                                {['@新人', '{新人昵称}', '{群名}', '{时间}', '{邀请人}'].map((placeholder) => (
                                    <button
                                        key={placeholder}
                                        type="button"
                                        className="text-xs bg-gray-100 hover:bg-gray-200 text-soft px-2 py-1 rounded"
                                        onClick={() => insertPlaceholder(placeholder)}
                                    >
                                        {placeholder}
                                    </button>
                                ))}
                            </div>
                            <textarea
                                ref={textareaRef}
                                className="textarea"
                                rows={6}
                                value={draft.content}
                                onChange={(event) => setDraft({...draft, content: event.target.value})}
                                placeholder="输入欢迎语内容..."
                            />
                            <div className="flex justify-end pt-2">
                                <button type="button" className="btn-primary" onClick={handleSave}>
                                    保存修改
                                </button>
                            </div>
                        </>
                    ) : (
                        <div className="flex-1 flex items-center justify-center text-sm text-muted">请选择一个群聊</div>
                    )}
                </div>
            </div>
        </section>
    )
}
